import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Logo from '../../logo/Logo.svg';

/**
 * Single layout for every pre-login page (login, register, forgot-pw,
 * change-pw, verify, not-found). Left brand panel with amber glow +
 * logo + tagline; right-centred paper card for the form. Collapses to
 * stacked card-only on narrow viewports.
 */
const AuthShell = ({ title, subtitle, children, footer, slim = false }) => {
    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: slim ? '1fr' : '1fr 1fr' },
                background:
                    'radial-gradient(900px 500px at 80% -10%, rgba(247,179,29,0.22), transparent 65%),' +
                    'radial-gradient(700px 400px at -10% 110%, rgba(247,179,29,0.15), transparent 60%),' +
                    'linear-gradient(180deg, #ffffff 0%, #FFFBF1 100%)',
            }}
        >
            {/* Left brand panel — hidden on mobile and in `slim` mode */}
            {!slim && (
                <Box
                    sx={{
                        display: { xs: 'none', md: 'flex' },
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        px: { md: 8, lg: 12 },
                        py: 8,
                        gap: 3,
                    }}
                >
                    <Box component="img" src={Logo} alt="Beta Appstore" sx={{ width: 180, height: 'auto' }} />
                    <Typography variant="h1" sx={{ maxWidth: 520 }}>
                        Ship beta builds to testers in seconds.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{ color: 'text.secondary', maxWidth: 460, fontSize: 16 }}
                    >
                        One shareable link for your team, your QA, and your clients.
                        iOS OTA install, Android APK, release notes, QR code — all built in.
                    </Typography>

                    <Stack direction="row" spacing={3} sx={{ mt: 2, color: 'text.secondary', fontSize: 13 }}>
                        <Link component={RouterLink} to="/terms-of-use" color="inherit">Terms of use</Link>
                        <Link component={RouterLink} to="/privacy-policy" color="inherit">Privacy policy</Link>
                    </Stack>
                </Box>
            )}

            {/* Right form card */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: { xs: 2, sm: 4 },
                    py: { xs: 4, md: 6 },
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        width: '100%',
                        maxWidth: 460,
                        px: { xs: 3, sm: 5 },
                        py: { xs: 4, sm: 5 },
                        borderRadius: 4,
                        border: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    {/* Compact brand mark — visible on mobile when left panel is hidden */}
                    <Box
                        sx={{
                            display: { xs: 'flex', md: slim ? 'flex' : 'none' },
                            justifyContent: 'center',
                            mb: 3,
                        }}
                    >
                        <Box component="img" src={Logo} alt="Beta Appstore" sx={{ width: 140, height: 'auto' }} />
                    </Box>

                    {title && (
                        <Typography variant="h2" sx={{ mb: 0.5, fontSize: { xs: 22, md: 26 } }}>
                            {title}
                        </Typography>
                    )}
                    {subtitle && (
                        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                            {subtitle}
                        </Typography>
                    )}

                    {children}

                    {footer && (
                        <Box sx={{ mt: 3, pt: 2.5, borderTop: '1px solid', borderColor: 'divider' }}>
                            {footer}
                        </Box>
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

export default AuthShell;
